<template>
  <!-- 独立页面（登录 / 404）：不显示任何布局，优先级最高 -->
  <div v-if="isPlainPage" class="plain-page">
    <router-view />
  </div>

  <!-- 移动端布局 -->
  <MobileLayout v-else-if="isMobile && showLayout" />

  <!-- 移动端大屏页面：仅渲染路由 -->
  <div v-else-if="isMobile" class="mobile-fullscreen">
    <router-view />
  </div>

  <!-- 使用路由元信息判断是否需要显示布局 -->
  <div v-else-if="showLayout" class="common-layout" :class="{ 'is-collapsed': isCollapsed }">
    <el-container>
      <el-header>
        <Header></Header>
      </el-header>
      <el-container class="body-container">
        <el-aside :width="asideWidth" class="app-aside">
          <LeftMenu :collapse="isCollapsed" />
        </el-aside>
        <el-main>
          <router-view/>
        </el-main>
      </el-container>
    </el-container>

    <!-- 折叠手柄：垂直居中贴于侧边栏右缘 -->
    <div
      class="toggle-button"
      :class="{ collapsed: isCollapsed }"
      @click="toggleAside"
    >
      <el-icon :size="14">
        <component :is="isCollapsed ? ArrowRight : ArrowLeft" />
      </el-icon>
      <span class="tooltip-text">{{ isCollapsed ? '展开菜单' : '收起菜单' }}</span>
    </div>
  </div>

  <!-- 大屏页面 - 不显示布局 -->
  <div v-else class="big-screen-layout">
    <router-view v-slot="{ Component }">
      <keep-alive include="BigScreen,LargeScreen">
        <component :is="Component" />
      </keep-alive>
    </router-view>
  </div>

  <!-- 全局悬浮组件：所有页面均可见（登录页除外） -->
  <template v-if="!isPlainPage">
    <RewardButton />
    <DraggableAssistantButton />
  </template>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import { useRoute } from 'vue-router'
import { ArrowLeft, ArrowRight } from '@element-plus/icons-vue'
import Header from '@/components/Header'
import LeftMenu from '@/components/LeftMenu'
import MobileLayout from '@/layout/MobileLayout.vue'
import RewardButton from '@/components/RewardButton.vue'
import DraggableAssistantButton from '@/views/DraggableAssistantButton.vue'
import { isMobile, watchDevice } from '@/utils/device'

const route = useRoute()

// 设备检测（移动端走独立布局）
let stopWatchDevice = null
onMounted(() => {
  stopWatchDevice = watchDevice()
})
onBeforeUnmount(() => {
  stopWatchDevice?.()
})

// 是否显示布局（侧边栏+标题栏）
const showLayout = computed(() => {
  // 根据路由元信息判断是否需要显示布局
  // 如果路由有 meta.hideLayout 为 true，则不显示布局（用于大屏页面）
  return !route.meta?.hideLayout
})

// 独立页面（登录页 / 404）：完全脱离应用框架
const isPlainPage = computed(
  () => route.meta?.public === true || route.meta?.plainLayout === true
)

// 侧边栏展开状态
const isCollapsed = ref(false)

// 计算侧边栏宽度
const asideWidth = computed(() => isCollapsed.value ? '64px' : '200px')

// 切换侧边栏展开状态
const toggleAside = () => {
  isCollapsed.value = !isCollapsed.value
}

// 监听路由变化，重置侧边栏状态（可选）
watch(
  () => route.path,
  () => {
    // 切换到大屏页面时自动展开侧边栏
    if (!showLayout.value) {
      isCollapsed.value = false
    }
  }
)

// ResizeObserver 防抖处理
const debounce = (fn, delay) => {
  let timer = null;
  return function () {
    let context = this;
    let args = arguments;
    clearTimeout(timer);
    timer = setTimeout(function () {
      fn.apply(context, args);
    }, delay);
  }
}

const _ResizeObserver = window.ResizeObserver;
window.ResizeObserver = class ResizeObserver extends _ResizeObserver {
  constructor(callback) {
    callback = debounce(callback, 16);
    super(callback);
  }
}
</script>
<style lang="less" scoped>
// ============================================================
// 整体布局：深色框架 + 浅色内容
// ============================================================
.common-layout {
  height: 100vh;
  background: #f0f2f5;
  overflow: hidden;

  .el-container {
    height: 100%;
  }

  .el-header {
    position: relative;
    z-index: 10;
    height: 64px !important;
    padding: 0;
    overflow: hidden;
    flex-shrink: 0;
  }

  .body-container {
    height: calc(100% - 64px);
  }

  // ===== 侧边栏：深蓝渐变 =====
  .app-aside {
    position: relative;
    background: linear-gradient(180deg, #1f3a5f 0%, #16283f 100%);
    border-right: none;
    transition: width 0.32s cubic-bezier(0.4, 0, 0.2, 1);
    overflow: hidden;
    box-shadow: 2px 0 20px rgba(0, 0, 0, 0.18);
    flex-shrink: 0;

    // 顶部微光装饰
    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 1px;
      background: linear-gradient(
        90deg,
        transparent 0%,
        rgba(64, 158, 255, 0.6) 50%,
        transparent 100%
      );
      pointer-events: none;
    }
  }

  // ===== 内容区 =====
  .el-main {
    padding: 20px;
    background: #f0f2f5;
    overflow-y: auto;
    scroll-behavior: smooth;

    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-thumb {
      background: #d0d7e2;
      border-radius: 3px;

      &:hover {
        background: #b4bfcd;
      }
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }
  }
}

// ============================================================
// 折叠手柄：垂直居中，贴于侧边栏右缘
// ============================================================
.toggle-button {
  position: fixed;
  top: 50%;
  left: calc(v-bind(asideWidth) - 13px);
  transform: translateY(-50%);
  z-index: 2000;
  width: 26px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: rgba(255, 255, 255, 0.75);
  background: linear-gradient(180deg, #2b4a70 0%, #1f3a5f 100%);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-left: none;
  border-radius: 0 14px 14px 0;
  box-shadow: 4px 2px 16px rgba(0, 0, 0, 0.28);
  transition: left 0.32s cubic-bezier(0.4, 0, 0.2, 1),
    background 0.25s ease, color 0.25s ease, box-shadow 0.25s ease;

  .el-icon {
    transition: transform 0.32s cubic-bezier(0.4, 0, 0.2, 1);
  }

  &:hover {
    color: #fff;
    background: linear-gradient(135deg, #409eff 0%, #2d7dd2 100%);
    border-color: #409eff;
    box-shadow: 5px 4px 22px rgba(64, 158, 255, 0.55);

    .el-icon {
      transform: scale(1.12);
    }
  }

  &:active {
    .el-icon {
      transform: scale(0.88);
    }
  }
}

// 悬停提示气泡
.tooltip-text {
  position: absolute;
  left: calc(100% + 14px);
  top: 50%;
  transform: translateY(-50%) translateX(-6px);
  padding: 6px 13px;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.3px;
  color: #fff;
  white-space: nowrap;
  background: rgba(32, 40, 52, 0.95);
  border-radius: 7px;
  opacity: 0;
  pointer-events: none;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.3);
  transition: opacity 0.25s ease, transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);

  &::before {
    content: '';
    position: absolute;
    left: -4px;
    top: 50%;
    transform: translateY(-50%) rotate(45deg);
    width: 8px;
    height: 8px;
    background: rgba(32, 40, 52, 0.95);
    border-radius: 1px;
  }
}

.toggle-button:hover .tooltip-text {
  opacity: 1;
  transform: translateY(-50%) translateX(0);
}

// ============================================================
// 独立页面容器（登录 / 404）
// ============================================================
.plain-page {
  width: 100%;
  height: 100%;
  overflow-y: auto;
  background: #f0f2f5;
}

// ============================================================
// 移动端大屏容器
// ============================================================
.mobile-fullscreen {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  height: 100dvh;
  overflow-y: auto;
  background: #0c2461;
}

// ============================================================
// 大屏布局
// ============================================================
.big-screen-layout {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 9999;
  overflow: hidden;
  background: transparent;

  &::-webkit-scrollbar {
    display: none;
  }
}

// ============================================================
// 入场动画（仅 opacity，避免 transform 影响 fixed 定位）
// ============================================================
.common-layout {
  animation: layoutFadeIn 0.4s ease-out;
}

@keyframes layoutFadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

// ============================================================
// 响应式
// ============================================================
@media (max-width: 768px) {
  .toggle-button {
    width: 22px;
    height: 46px;
    border-radius: 0 11px 11px 0;
  }

  .common-layout .el-main {
    padding: 12px;
  }
}
</style>
